import { useState } from "react";
import { cjsDefault } from "../../lib/cjs-default";
import ApiVideoPlayer from "@api.video/react-player";
import { IconAlertTriangle } from "@tabler/icons-react";
import reactPlayer from "react-player";
import { Button } from "../../components/ui/Button";
import type { Api } from "../../services/api";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventVideoPlay } from "../../tools/analytics/domains/video.analytics";
import { useVideoProgress } from "../../tools/analytics/useContentProgress";

const ReactPlayer = cjsDefault(reactPlayer);

type VideoDetail = Extract<
  Api["get"]["/webapp/public/content/:id"]["output"]["body"],
  { content_format: "Video" }
>;

const YOUTUBE_CONFIG = {
  playerVars: {
    showinfo: 0,
    modestbranding: 1,
    iv_load_policy: 3,
    rel: 0,
    wmode: "transparent",
    playsinline: 1,
    controls: 1,
    fs: 1,
    disablekb: 1,
  },
};

function normalizeVideoSourceURL(url: string): string {
  if (!URL.canParse(url)) return url;
  const parsed = new URL(url);
  const host = parsed.hostname.replace(/^www\./, "");
  if (host === "youtube.com" || host === "m.youtube.com") {
    const id = parsed.searchParams.get("v");
    if (id !== null) return `https://www.youtube.com/watch?v=${id}`;
  }
  if (host === "youtu.be") {
    return `https://youtu.be${parsed.pathname}`;
  }
  return url;
}

/**
 * What the player shows when the video fails to load. `native`: whatever the
 * player draws, as on the phone. `panel`: a message in the player's frame with
 * a retry that reloads the player alone (Figma "D3.video.error").
 */
export type VideoFailureView =
  | { kind: "native" }
  | {
      kind: "panel";
      title: string;
      description: string;
      retryLabel: string;
    };

/**
 * The video of a video page: the api.video player for a video hosted there,
 * else the source url (YouTube, Vimeo, a file) through react-player. It fills
 * its parent.
 *
 * The failure and the retry count are player machinery, kept here: a retry
 * remounts the player under a new key.
 */
export function VideoPlayer({
  video,
  failureView,
}: {
  video: VideoDetail;
  failureView: VideoFailureView;
}): React.JSX.Element {
  const [attempt, setAttempt] = useState(0);
  const [hasFailed, setHasFailed] = useState(false);
  const videoProgress = useVideoProgress(video.id);
  const onError = () => {
    setHasFailed(true);
  };
  const onPlay = () => {
    gaEventVideoPlay({
      video,
      listName: AnalyticsItemList.VIDEO_ARTICLE_PAGE,
    });
  };

  if (hasFailed && failureView.kind === "panel") {
    return (
      <div className="flex size-full flex-col items-center justify-center gap-3 bg-ds-black px-6 text-center font-dm-sans text-ds-white">
        <IconAlertTriangle className="size-8" stroke={1.5} />
        <p className="m-0 text-desktop-card-title font-semibold">
          {failureView.title}
        </p>
        <p className="m-0 text-ui-body text-ds-white/70">
          {failureView.description}
        </p>
        <Button
          type="button"
          variant="secondary"
          size="md"
          className="mt-2"
          onClick={() => {
            setHasFailed(false);
            setAttempt((current) => current + 1);
          }}
        >
          {failureView.retryLabel}
        </Button>
      </div>
    );
  }

  if (video.apiVideoId !== null) {
    return (
      <ApiVideoPlayer
        key={attempt}
        video={{ id: video.apiVideoId }}
        style={{ width: "100%", height: "100%" }}
        videoStyleObjectFit="contain"
        autoplay
        onPlay={onPlay}
        onError={onError}
        onTimeUpdate={videoProgress.onPlayhead}
        onDurationChange={videoProgress.onDuration}
        controls={[
          "play",
          "seekBackward",
          "seekForward",
          "playbackRate",
          "volume",
          "progressBar",
          "fullscreen",
        ]}
      />
    );
  }
  return (
    <ReactPlayer
      key={attempt}
      width="100%"
      height="100%"
      url={
        video.sourceURL === null
          ? undefined
          : normalizeVideoSourceURL(video.sourceURL)
      }
      config={{ youtube: { ...YOUTUBE_CONFIG } }}
      onPlay={onPlay}
      onError={onError}
      onProgress={(state) => {
        videoProgress.onPlayhead(state.playedSeconds);
      }}
      onDuration={videoProgress.onDuration}
      stopOnUnmount
      playsinline
      controls
    />
  );
}
